import { useState, useMemo, useCallback, useEffect } from 'react';
import { computeHealth } from '../utils/health.js';
import { computeStage } from '../utils/stage.js';
import { computeUrgency } from '../utils/urgency.js';
import * as XLSX from 'xlsx';
import { appendOutreachLog, updateDemo, createTrial, patchReplyType, fetchBdHighIntent, fetchCampaignStats, fetchBdFollowupList, classifyReplies, updateContact, fetchCadence } from '../api/backend.js';
import { fetchContactCandidates } from '../api/bigdough.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function relDate(ts) {
  if (!ts) return 'Never';
  const d = new Date(ts);
  if (isNaN(d)) return '—';
  const diffMs = Date.now() - d.getTime();
  const hrs = diffMs / 3_600_000;
  const days = diffMs / 86_400_000;
  if (hrs < 1) return 'Just now';
  if (hrs < 24) return `${Math.floor(hrs)}h ago`;
  if (days < 14) return `${Math.floor(days)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function startOfWeek() {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatClock(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDateLabel(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getContactName(contact, firmName) {
  return contact?.name || firmName || contact?.email || 'Untitled';
}

function laneSummary(count, xp, extra) {
  const parts = [`${count} mission${count === 1 ? '' : 's'}`];
  if (xp != null) parts.push(`${xp} XP`);
  if (extra) parts.push(extra);
  return parts.join(' · ');
}

function displayNameFrom(email, name) {
  if (name && String(name).trim()) return String(name).trim().split(/\s+/)[0];
  if (!email) return 'there';
  const local = String(email).split('@')[0] || '';
  return local.replace(/[._-]+/g, ' ').trim().split(/\s+/).filter(Boolean).map((p) => p[0].toUpperCase() + p.slice(1)).join(' ') || 'there';
}

function LaneChip({ tone = 'gray', children }) {
  return <span className={`chip chip-${tone}`}>{children}</span>;
}

function resolveDemoAttendees(demo, contactByEmail, contactById) {
  const raw = demo.attendee_emails ?? '';
  if (!raw) return [];
  return raw.split(',').map((e) => e.trim().toLowerCase()).filter(Boolean).map((email) => {
    const c = contactByEmail[email] ?? contactById[email] ?? null;
    return c ? { name: c.name || '', email, contact_id: c.contact_id } : { name: '', email };
  });
}

// ── Remind Later Picker ───────────────────────────────────────────────────────

function RemindLaterPicker({ onConfirm, onCancel, todayStr }) {
  const [date, setDate] = useState('');
  const days = date ? Math.round((new Date(date).getTime() - new Date(todayStr + 'T00:00:00').getTime()) / 86_400_000) : 0;
  return (
    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="date"
          value={date}
          min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
          onChange={(e) => setDate(e.target.value)}
          style={{ fontSize: 11, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface-2)', color: 'var(--text)' }}
        />
        <button className="btn btn--primary btn--sm" style={{ fontSize: 11 }} disabled={!date || days <= 0}
          onClick={() => date && days > 0 && onConfirm(date)}>Set</button>
        <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={onCancel}>Cancel</button>
      </div>
      {date && days > 0 && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>in {days} day{days !== 1 ? 's' : ''}</span>}
    </div>
  );
}

// ── Follow Up Lane ────────────────────────────────────────────────────────────

function gmailThreadUrl(threadId) {
  if (!threadId) return null;
  return `https://mail.google.com/mail/u/0/#all/${threadId}`;
}

function ReplyCard({ r, contact, firm, chipTone = 'amber', chipLabel = 'AI: Followup', onOpenTimeline, onOverride, showRepliedButton = false, onRemindLater }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [repliedToast, setRepliedToast] = useState(false);
  const name = contact?.name || firm?.firm_name || 'Unknown sender';
  const firmLabel = firm?.firm_name && firm.firm_name !== name ? firm.firm_name : '';
  const subject = (r.subject ?? '').trim();

  const handleOverride = async (replyType) => {
    setMenuOpen(false);
    setSaving(true);
    if (replyType === 'replied') setRepliedToast(true);
    await onOverride(r.gmail_thread_id || r.log_id, replyType, { logId: r.log_id });
    setSaving(false);
  };

  return (
    <div className={`reply-item${chipTone === 'amber' ? ' reply-item--flagged' : ''}`} style={{ alignItems: 'flex-start', opacity: saving ? 0.5 : 1 }}>
      <span style={{ fontSize: 14, flexShrink: 0, marginTop: 2 }}>📨</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        {firmLabel && <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{firmLabel}</div>}
        {subject && <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>"{subject}"</div>}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className={`chip chip-${chipTone}`} style={{ fontSize: 10 }}>{chipLabel}</span>
          <span>{relDate(r.date)}</span>
        </div>
        {pickerOpen && onRemindLater && (
          <RemindLaterPicker
            todayStr={new Date().toLocaleDateString('sv')}
            onConfirm={(date) => { onRemindLater(date); setPickerOpen(false); }}
            onCancel={() => setPickerOpen(false)}
          />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0, position: 'relative' }}>
        {contact?.contact_id && <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={() => onOpenTimeline?.(contact)}>Profile</button>}
        {showRepliedButton && !repliedToast && (
          <button className="btn btn--primary btn--sm" style={{ fontSize: 11 }} disabled={saving} onClick={() => handleOverride('replied')}>
            Replied ✓
          </button>
        )}
        {repliedToast && (
          <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>✓ Marked replied</span>
        )}
        {onRemindLater && (
          <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={() => setPickerOpen((v) => !v)}>⏰ Remind</button>
        )}
        <div style={{ position: 'relative' }}>
          <button className="btn btn--ghost btn--sm" style={{ fontSize: 11, width: '100%' }} onClick={() => setMenuOpen((v) => !v)}>Edit ▾</button>
          {menuOpen && (
            <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 148, overflow: 'hidden' }}>
              <button className="btn btn--ghost btn--sm" style={{ width: '100%', textAlign: 'left', borderRadius: 0, fontSize: 12, padding: '7px 12px' }} onClick={() => handleOverride('other')}>Dismiss (not relevant)</button>
              <button className="btn btn--ghost btn--sm" style={{ width: '100%', textAlign: 'left', borderRadius: 0, fontSize: 12, padding: '7px 12px', color: 'var(--red, #e53e3e)' }} onClick={() => handleOverride('unsubscribe')}>Mark Unsubscribe</button>
              <button className="btn btn--ghost btn--sm" style={{ width: '100%', textAlign: 'left', borderRadius: 0, fontSize: 12, padding: '7px 12px', color: 'var(--amber, #d97706)' }} onClick={() => handleOverride('bounce')}>Mark Bounced</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FollowUpLane({
  followupItems,
  sortedReminders,
  todayStr,
  onOpenTimeline,
  clearBatchReminder,
  onOverrideReplyType,
  activeBdSuggestions,
  onSnoozeFollowupIncoming,
  onSnoozeBdSuggestion,
  onDismissBdSuggestion,
  onRepliedBdSuggestion,
}) {
  const [incomingOpen, setIncomingOpen] = useState(true);
  const [futureRemindersOpen, setFutureRemindersOpen] = useState(false);
  const [bdOpen, setBdOpen] = useState(true);

  const dueReminders = sortedReminders.filter(({ due }) => due <= todayStr);
  const futureReminders = sortedReminders.filter(({ due }) => due > todayStr);

  return (
    <div className="lane lane--followup">
      <div className="lane-header">
        <div className="lane-header__title">Follow Up</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          <LaneChip tone="gray">{followupItems.length} incoming</LaneChip>
          {followupItems.length > 0 && <LaneChip tone="red">needs response</LaneChip>}
        </div>
      </div>
      <div className="lane-body">

        {/* BD Suggestions */}
        {activeBdSuggestions.length > 0 && (
          <>
            <button
              className="sub-label"
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: 0, width: '100%', textAlign: 'left', marginBottom: 6 }}
              onClick={() => setBdOpen((v) => !v)}
            >
              <span>🎯 BD Suggestions</span>
              <LaneChip tone="purple">{activeBdSuggestions.length}</LaneChip>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>{bdOpen ? '▲' : '▼'}</span>
            </button>
            {bdOpen && activeBdSuggestions.map((s) => {
              const key = s.contact_id || s.email;
              return (
                <BdSuggestionCard
                  key={key}
                  suggestion={s}
                  todayStr={todayStr}
                  onOpenTimeline={onOpenTimeline}
                  onSnooze={(date) => onSnoozeBdSuggestion?.(key, date)}
                  onDismiss={() => onDismissBdSuggestion?.(key)}
                  onReplied={() => onRepliedBdSuggestion?.(key, s)}
                />
              );
            })}
          </>
        )}

        {/* Incoming — needs response */}
        <button
          className="sub-label"
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: 0, width: '100%', textAlign: 'left', marginTop: activeBdSuggestions.length > 0 ? 8 : 0 }}
          onClick={() => setIncomingOpen((v) => !v)}
        >
          <span>Incoming — Needs Response</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{incomingOpen ? '▲' : '▼'}</span>
        </button>
        {incomingOpen && (
          followupItems.length === 0
            ? <div className="empty-lane" style={{ marginBottom: 12 }}><div className="empty-lane__icon">✉️</div><div>No pending replies</div></div>
            : followupItems.map(({ log: r, contact, firm }) => {
                const itemKey = r.contact_id || r.gmail_thread_id;
                return (
                  <ReplyCard
                    key={r.log_id || r.gmail_thread_id}
                    r={r} contact={contact} firm={firm}
                    onOpenTimeline={onOpenTimeline}
                    onOverride={onOverrideReplyType}
                    showRepliedButton
                    onRemindLater={(date) => onSnoozeFollowupIncoming?.(itemKey, date)}
                  />
                );
              })
        )}

        {/* Reminders — due */}
        {dueReminders.length > 0 && (
          <>
            <div className="sub-label" style={{ marginTop: 8 }}>⏰ Reminders</div>
            {dueReminders.map(({ key, due }) => {
              const [day, ...rest] = key.split('||');
              const subject = rest.join('||');
              const diffDays = Math.ceil((new Date(due + 'T00:00:00').getTime() - new Date(todayStr + 'T00:00:00').getTime()) / 86_400_000);
              const isOverdue = diffDays < 0;
              const isDueToday = diffDays === 0;
              const dueLabel = isOverdue ? `${Math.abs(diffDays)}d overdue` : isDueToday ? 'Due today' : `Due in ${diffDays}d`;
              return (
                <div key={key} className="reminder-card">
                  <div className="reminder-card__subject">{subject}</div>
                  <div className="reminder-card__meta">Sent {day} · {dueLabel}</div>
                  <div className="reminder-card__actions">
                    <button className="btn btn--primary btn--sm" style={{ fontSize: 11 }} onClick={() => clearBatchReminder(key)}>Followed up ✓</button>
                    <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={() => clearBatchReminder(key)}>Dismiss</button>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* Reminders — future (collapsed by default) */}
        {futureReminders.length > 0 && (
          <>
            <button
              className="sub-label"
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: 0, width: '100%', textAlign: 'left', marginTop: dueReminders.length > 0 ? 6 : 8 }}
              onClick={() => setFutureRemindersOpen((v) => !v)}
            >
              <span>{futureReminders.length} upcoming</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{futureRemindersOpen ? '▲' : '▼'}</span>
            </button>
            {futureRemindersOpen && futureReminders.map(({ key, due }) => {
              const [day, ...rest] = key.split('||');
              const subject = rest.join('||');
              const diffDays = Math.ceil((new Date(due + 'T00:00:00').getTime() - new Date(todayStr + 'T00:00:00').getTime()) / 86_400_000);
              const dueLabel = diffDays === 1 ? 'Due tomorrow' : `Due in ${diffDays}d`;
              return (
                <div key={key} className="reminder-card">
                  <div className="reminder-card__subject">{subject}</div>
                  <div className="reminder-card__meta">Sent {day} · {dueLabel}</div>
                  <div className="reminder-card__actions">
                    <button className="btn btn--primary btn--sm" style={{ fontSize: 11 }} onClick={() => clearBatchReminder(key)}>Followed up ✓</button>
                    <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={() => clearBatchReminder(key)}>Dismiss</button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function BdSuggestionCard({ suggestion: s, todayStr, onOpenTimeline, onSnooze, onDismiss, onReplied }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const name = s.name || s.email || 'Unknown';
  const signals = [];
  if (s.read_count >= 2) signals.push(`Opened ${s.read_count}×`);
  if (s.url_link_views > 0) signals.push(`Clicked link ${s.url_link_views}×`);
  if (s.file_link_views > 0) signals.push(`Viewed file ${s.file_link_views}×`);
  const contact = s.contact_id ? { contact_id: s.contact_id, name: s.name, email: s.email } : null;

  return (
    <div className="reply-item reply-item--flagged" style={{ alignItems: 'flex-start' }}>
      <span style={{ fontSize: 14, flexShrink: 0, marginTop: 2 }}>🎯</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        {s.firm_name && <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.firm_name}</div>}
        {s.campaign_subject && <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>"{s.campaign_subject}"</div>}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {signals.map((sig, i) => <span key={i} className="chip chip-purple" style={{ fontSize: 10 }}>{sig}</span>)}
        </div>
        {pickerOpen && (
          <RemindLaterPicker
            todayStr={todayStr}
            onConfirm={(date) => { onSnooze?.(date); setPickerOpen(false); }}
            onCancel={() => setPickerOpen(false)}
          />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
        {contact && <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={() => onOpenTimeline?.(contact)}>Profile</button>}
        <button className="btn btn--primary btn--sm" style={{ fontSize: 11 }} onClick={onReplied}>Replied ✓</button>
        <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={() => setPickerOpen((v) => !v)}>⏰ Remind</button>
        <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={onDismiss}>Dismiss</button>
      </div>
    </div>
  );
}

// ── Fuzzy subject matching ────────────────────────────────────────────────────

function normalizeSubject(s) {
  return s.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

function subjectSimilarity(a, b) {
  const na = normalizeSubject(a);
  const nb = normalizeSubject(b);
  const wa = new Set(na.split(' ').filter((w) => w.length > 2));
  const wb = new Set(nb.split(' ').filter((w) => w.length > 2));
  const wInter = [...wa].filter((w) => wb.has(w)).length;
  const wUnion = new Set([...wa, ...wb]).size;
  const wordJaccard = wUnion === 0 ? 1 : wInter / wUnion;
  if (wordJaccard >= 0.6) return wordJaccard;
  const tA = new Set();
  const tB = new Set();
  for (let i = 0; i <= na.length - 3; i++) tA.add(na.slice(i, i + 3));
  for (let i = 0; i <= nb.length - 3; i++) tB.add(nb.slice(i, i + 3));
  const tInter = [...tA].filter((t) => tB.has(t)).length;
  const tUnion = new Set([...tA, ...tB]).size;
  return tUnion === 0 ? 1 : tInter / tUnion;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const SIGNAL_COLORS = { red: 'var(--red)', amber: 'var(--amber)', green: 'var(--green)', gray: 'var(--text-3)' };

const LABEL_STYLE = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-3)',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
};

const NO_CONTACT_BADGE = (
  <span title="No contact linked" style={{
    fontSize: 10, background: '#fef3c7', color: '#92400e',
    border: '1px solid #fcd34d', borderRadius: 4, padding: '1px 5px', fontWeight: 600,
  }}>⚠ No contact</span>
);

// ── Sub-components ────────────────────────────────────────────────────────────

function MissionCard({ tone = 'gray', title, subtitle, xp, signals = [], ai, actions }) {
  return (
    <div className={`mission-card mission-card--${tone}`}>
      <div className="mc-header"><div className="mc-name">{title}</div></div>
      <div className="mc-firm">{subtitle}</div>
      {signals.map((sig, i) => (
        <div key={i} className="mc-signal">
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: SIGNAL_COLORS[tone] ?? SIGNAL_COLORS.gray, flexShrink: 0, display: 'inline-block' }} />
          {sig}
        </div>
      ))}
      <div className="mc-actions">{actions}</div>
      {(ai || xp) && (
        <div className="mc-xp-footer">
          <span style={{ color: 'var(--orange)' }}>✦</span>
          {ai && <span>{ai}</span>}
          {xp && <span style={{ marginLeft: 'auto' }}>{xp}</span>}
        </div>
      )}
    </div>
  );
}

// ── Quick Log Modal ───────────────────────────────────────────────────────────

function QuickLogModal({ contact, firmName, getToken, onClose, onSaved }) {
  const todayStr = new Date().toLocaleDateString('sv');
  const [channel, setChannel] = useState('Email');
  const [direction, setDirection] = useState('Outbound');
  const [notes, setNotes] = useState('');
  const [logDate, setLogDate] = useState(todayStr);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const result = await appendOutreachLog(getToken, {
        contact_id: contact.contact_id ?? '',
        firm_id: contact.firm_id ?? '',
        date: logDate || todayStr,
        channel, direction, type: channel, notes,
      });
      onSaved(result?.gamification ?? null);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div className="card" style={{ width: 360, padding: '24px 28px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Log Touch</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
          {contact.name || contact.email}{firmName ? ` · ${firmName}` : ''}
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Channel</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {['Email', 'Call', 'Meeting', 'LinkedIn', 'Conference'].map((ch) => (
              <button key={ch} type="button" className={`btn btn--sm ${channel === ch ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setChannel(ch)}>{ch}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Direction</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {['Outbound', 'Inbound'].map((d) => (
              <button key={d} type="button" className={`btn btn--sm ${direction === d ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setDirection(d)}>{d}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Date</div>
          <input
            type="date" value={logDate} max={todayStr}
            onChange={(e) => setLogDate(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--surface-2)', color: 'var(--text)', marginBottom: 14, boxSizing: 'border-box' }}
          />
          <textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            style={{ width: '100%', resize: 'vertical', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, fontFamily: 'inherit', marginBottom: 14, boxSizing: 'border-box', background: 'var(--surface-2)', color: 'var(--text)' }}
          />
          {error && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 10 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>{saving ? 'Saving…' : 'Log it'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Demo Outcome Modal ────────────────────────────────────────────────────────

function DemoOutcomeModal({ demo, contact, firm, getToken, onClose, onSaved, onStartTrial }) {
  const [outcome, setOutcome] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleNextStep(nextStep) {
    if (!outcome.trim()) { setError('Enter an outcome before continuing.'); return; }
    setSaving(true);
    setError('');
    try {
      await updateDemo(getToken, demo.demo_id, { outcome, status: 'Completed' });
      if (nextStep === 'followup') {
        await appendOutreachLog(getToken, {
          contact_id: contact.contact_id ?? '',
          firm_id: firm?.firm_id ?? contact?.firm_id ?? '',
          date: new Date().toISOString().slice(0, 10),
          channel: 'Meeting', direction: 'Outbound', type: 'Demo',
          notes: outcome, reply_type: 'need_followup',
        });
        onSaved();
      } else if (nextStep === 'trial') {
        onStartTrial({ outcome });
      } else {
        onSaved();
      }
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div className="card" style={{ width: 420, padding: '24px 28px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Log Demo Outcome</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
          {contact?.name || contact?.email || '—'}{firm?.firm_name ? ` · ${firm.firm_name}` : ''}{demo.scheduled_date ? ` · ${formatDateLabel(demo.scheduled_date)}` : ''}
        </div>
        <div style={{ ...LABEL_STYLE, marginBottom: 6 }}>What happened?</div>
        <textarea
          placeholder="e.g. Strong interest, wants to start a trial. Budget confirmed."
          value={outcome} onChange={(e) => setOutcome(e.target.value)} rows={4} autoFocus
          style={{ width: '100%', resize: 'vertical', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, fontFamily: 'inherit', marginBottom: 16, boxSizing: 'border-box', background: 'var(--surface-2)', color: 'var(--text)' }}
        />
        {error && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 10 }}>{error}</div>}
        <div style={{ ...LABEL_STYLE, marginBottom: 8 }}>What's next?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 }}>
          <button className="btn btn--primary btn--sm" disabled={saving} onClick={() => handleNextStep('trial')} style={{ justifyContent: 'flex-start' }}>
            🧪 Start Trial →
          </button>
          <button className="btn btn--ghost btn--sm" disabled={saving} onClick={() => handleNextStep('followup')} style={{ justifyContent: 'flex-start' }}>
            📬 Add to Follow Up
          </button>
          <button className="btn btn--ghost btn--sm" disabled={saving} onClick={() => handleNextStep('none')} style={{ justifyContent: 'flex-start', color: 'var(--text-3)' }}>
            ✕ No next step
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Create Trial Modal ────────────────────────────────────────────────────────

function CreateTrialModal({ demo, contact, firm, outcomeText, getToken, onClose, onSaved, onTabChange, trialDefaultDays = 30 }) {
  const todayStr = new Date().toLocaleDateString('sv');
  const defaultEnd = new Date(Date.now() + trialDefaultDays * 86_400_000).toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [status, setStatus] = useState('Active');
  const [notes, setNotes] = useState(outcomeText || demo.outcome || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createTrial(getToken, {
        contact_id: contact?.contact_id ?? demo.contact_id ?? '',
        firm_id: firm?.firm_id ?? contact?.firm_id ?? demo.firm_id ?? '',
        start_date: startDate,
        end_date: endDate,
        status,
        notes,
      });
      onSaved();
      onTabChange?.('inprogress');
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  const fieldLabel = (text) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>{text}</div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={onClose}>
      <div className="card" style={{ width: 440, padding: '24px 28px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Start Trial</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
          {contact?.name || contact?.email || '—'}{firm?.firm_name ? ` · ${firm.firm_name}` : ''}
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px', marginBottom: 14 }}>
            <div>
              {fieldLabel('Start date')}
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--surface-2)', color: 'var(--text)', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              {fieldLabel('End date')}
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--surface-2)', color: 'var(--text)', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            {fieldLabel('Status')}
            <div style={{ display: 'flex', gap: 6 }}>
              {['Active', 'Completed', 'Cancelled'].map((s) => (
                <button key={s} type="button" className={`btn btn--sm ${status === s ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setStatus(s)}>{s}</button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            {fieldLabel('Notes')}
            <textarea placeholder="Trial context, demo outcome, expectations…" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              style={{ width: '100%', resize: 'vertical', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--surface-2)', color: 'var(--text)' }}
            />
          </div>
          {error && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 10 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>{saving ? 'Creating…' : 'Create Trial →'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Today({ data, getToken, onOpenTimeline, onTabChange, onNavigateToDemo, onNavigateToTrial, onRefreshOutreach, onRefreshAll, userEmail, userName, settings }) {
  const [quickLogTarget, setQuickLogTarget] = useState(null);
  const [xpBump, setXpBump] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('today');
  const [addContactBanners, setAddContactBanners] = useState(() => {
    try { return JSON.parse(localStorage.getItem('addContactBanners') || '[]'); } catch { return []; }
  });
  const [expandedBatches, setExpandedBatches] = useState({});
  const [batchReminders, setBatchReminders] = useState(() => {
    try { return JSON.parse(localStorage.getItem('batchReminders') || '{}'); } catch { return {}; }
  });
  const [dismissedBatches, setDismissedBatches] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dismissedBatches') || '[]')); } catch { return new Set(); }
  });
  const [reminderInput, setReminderInput] = useState({});
  const [demoOutcomeTarget, setDemoOutcomeTarget] = useState(null);
  const [createTrialTarget, setCreateTrialTarget] = useState(null);
  const [replyTypeOverrides, setReplyTypeOverrides] = useState({});
  const [classifying, setClassifying] = useState(false);
  const [classifyMsg, setClassifyMsg] = useState('');
  const [stageOverrides, setStageOverrides] = useState({});
  const [cadenceActionList, setCadenceActionList] = useState([]);
  const [specRotationNext, setSpecRotationNext] = useState(null);
  const [pendingBdCandidates, setPendingBdCandidates] = useState([]);

  // Snooze/dismiss — Trial Management
  const [snoozedTrials, setSnoozedTrials] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snoozedTrials') || '{}'); } catch { return {}; }
  });
  const [dismissedTrials, setDismissedTrials] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dismissedTrials') || '[]')); } catch { return new Set(); }
  });

  // Snooze/dismiss — Today demos
  const [snoozedTodayDemos, setSnoozedTodayDemos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snoozedTodayDemos') || '{}'); } catch { return {}; }
  });
  const [dismissedTodayDemos, setDismissedTodayDemos] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dismissedTodayDemos') || '[]')); } catch { return new Set(); }
  });

  // Snooze/dismiss — Needs Outcome
  const [snoozedNeedsOutcome, setSnoozedNeedsOutcome] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snoozedNeedsOutcome') || '{}'); } catch { return {}; }
  });
  const [dismissedNeedsOutcome, setDismissedNeedsOutcome] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dismissedNeedsOutcome') || '[]')); } catch { return new Set(); }
  });

  // Snooze/dismiss — Overdue Next Steps
  const [snoozedOverdueNextSteps, setSnoozedOverdueNextSteps] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snoozedOverdueNextSteps') || '{}'); } catch { return {}; }
  });
  const [dismissedOverdueNextSteps, setDismissedOverdueNextSteps] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dismissedOverdueNextSteps') || '[]')); } catch { return new Set(); }
  });

  // Snooze/dismiss — Next Step Follow-up
  const [snoozedNextSteps, setSnoozedNextSteps] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snoozedNextSteps') || '{}'); } catch { return {}; }
  });
  const [dismissedNextSteps, setDismissedNextSteps] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dismissedNextSteps') || '[]')); } catch { return new Set(); }
  });
  const [nextStepTrialTarget, setNextStepTrialTarget] = useState(null);
  const [nextStepDoneLoading, setNextStepDoneLoading] = useState(null);

  // Snooze — Follow Up incoming
  const [snoozedFollowupIncoming, setSnoozedFollowupIncoming] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snoozedFollowupIncoming') || '{}'); } catch { return {}; }
  });

  // BD Suggestions
  const [bdSuggestions, setBdSuggestions] = useState([]);
  const bdDays = Number(settings?.bdHighIntentDays ?? 14);
  const [dismissedBdSuggestions, setDismissedBdSuggestions] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dismissedBdSuggestions') || '[]')); } catch { return new Set(); }
  });
  const [repliedBdSuggestions, setRepliedBdSuggestions] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('repliedBdSuggestions') || '[]')); } catch { return new Set(); }
  });
  const [snoozedBdSuggestions, setSnoozedBdSuggestions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snoozedBdSuggestions') || '{}'); } catch { return {}; }
  });

  // BD campaign stats (for delivered count + upload_id matching)
  const [bdCampaignStats, setBdCampaignStats] = useState([]);
  const [bdStatsError, setBdStatsError] = useState(null);
  useEffect(() => {
    if (!getToken) return;
    fetchCampaignStats(getToken, 90).then((rows) => {
      if (Array.isArray(rows)) setBdCampaignStats(rows);
      setBdStatsError(null);
    }).catch((e) => {
      console.warn('[bd-campaign-stats] fetch failed:', e);
      setBdStatsError(e?.message || 'failed to load campaign stats');
    });
  }, [getToken]);

  // Pending BigDough contact-candidate review queue — nothing auto-clears this,
  // and a candidate's outreach rows carry no contact_id until approved, so a
  // stale queue means real touches are invisible everywhere else in the app.
  useEffect(() => {
    if (!getToken) return;
    fetchContactCandidates(getToken, { status: 'pending', limit: 500 }).then((rows) => {
      setPendingBdCandidates(Array.isArray(rows) ? rows : []);
    }).catch((e) => console.warn('[bd-contact-candidates] fetch failed:', e));
  }, [getToken]);

  // Cadence action list (cold-outreach follow-up schedule) — same GET /api/cadence
  // the Outreach → Cadence tab reads, just rendered compactly here too.
  useEffect(() => {
    if (!getToken) return;
    fetchCadence(getToken).then((res) => {
      setCadenceActionList(Array.isArray(res?.actionList) ? res.actionList : []);
      setSpecRotationNext(res?.specRotation?.next ?? null);
    }).catch((e) => {
      console.warn('[cadence] fetch failed:', e);
    });
  }, [getToken]);

  // Followup list export modal
  const [followupModal, setFollowupModal] = useState(null); // { batch, uploadId }
  const [followupCategories, setFollowupCategories] = useState({
    high_intent: true, opened: true, no_engagement: true, replied: false,
  });
  const [followupLoading, setFollowupLoading] = useState(false);

  // Remind Later picker (shared across main-component lanes)
  const [remindPickerKey, setRemindPickerKey] = useState(null);
  const [remindPickerDate, setRemindPickerDate] = useState('');

  function handleStageOverride(contactId, newOverride) {
    setStageOverrides((prev) => ({ ...prev, [contactId]: newOverride }));
    if (contactId && getToken) {
      updateContact(getToken, contactId, { stage_override: newOverride })
        .then(() => {
          onRefreshAll?.();
          onRefreshOutreach?.();
        })
        .catch(() => {});
    }
  }

  // Destructure early (with null-safe fallback) so hooks below can always run.
  const {
    contacts = [],
    contactEmailMap = [],
    firms = [],
    demos = [],
    trials = [],
    outreachLog = [],
    config = {},
    todaySummary = null,
  } = data ?? {};

  // Fetch BD high-intent suggestions
  useEffect(() => {
    if (!getToken) return;
    fetchBdHighIntent(getToken, bdDays).then((rows) => {
      if (Array.isArray(rows)) setBdSuggestions(rows);
    }).catch(() => {});
  }, [getToken, bdDays]);

  // Full contact lookup maps
  const contactById = useMemo(() => {
    const m = {};
    for (const c of contacts) { if (c.contact_id) m[c.contact_id] = c; }
    for (const c of contactEmailMap) { if (c.contact_id) m[c.contact_id] = c; }
    return m;
  }, [contacts, contactEmailMap]);

  const contactByEmail = useMemo(() => {
    const m = {};
    for (const c of contactEmailMap) { if (c.email) m[c.email.toLowerCase()] = c; }
    return m;
  }, [contactEmailMap]);

  const firmMap = useMemo(() => {
    const m = {};
    for (const f of firms) { if (f.firm_id) m[f.firm_id] = f; }
    return m;
  }, [firms]);

  const effectiveOutreachLog = useMemo(() => {
    if (Object.keys(replyTypeOverrides).length === 0) return outreachLog;
    return outreachLog.map((r) => {
      const key = (r.gmail_thread_id && replyTypeOverrides[r.gmail_thread_id] !== undefined)
        ? r.gmail_thread_id
        : (r.log_id && replyTypeOverrides[r.log_id] !== undefined ? r.log_id : null);
      return key ? { ...r, reply_type: replyTypeOverrides[key] } : r;
    });
  }, [outreachLog, replyTypeOverrides]);

  const handleOverrideReplyType = async (key, replyType, { logId } = {}) => {
    if (!key) return;
    setReplyTypeOverrides((prev) => {
      const n = { ...prev, [key]: replyType };
      if (logId && logId !== key) n[logId] = replyType;
      return n;
    });
    try {
      await patchReplyType(getToken, key, replyType);
      onRefreshOutreach?.();
      onRefreshAll?.();
    } catch (e) {
      console.error('patchReplyType failed:', e);
      setReplyTypeOverrides((prev) => {
        const n = { ...prev };
        delete n[key];
        if (logId) delete n[logId];
        return n;
      });
    }
  };

  const outreachByContact = useMemo(() => {
    const m = new Map();
    for (const r of outreachLog) {
      if (!r.contact_id) continue;
      const arr = m.get(r.contact_id);
      if (arr) arr.push(r); else m.set(r.contact_id, [r]);
    }
    return m;
  }, [outreachLog]);

  const demosByContact = useMemo(() => {
    const m = new Map();
    for (const d of demos) {
      if (!d.contact_id) continue;
      const arr = m.get(d.contact_id);
      if (arr) arr.push(d); else m.set(d.contact_id, [d]);
    }
    return m;
  }, [demos]);

  const trialsByContact = useMemo(() => {
    const m = new Map();
    for (const t of trials) {
      if (!t.contact_id) continue;
      const arr = m.get(t.contact_id);
      if (arr) arr.push(t); else m.set(t.contact_id, [t]);
    }
    return m;
  }, [trials]);

  const urgentContacts = useMemo(() => {
    if (Array.isArray(todaySummary?.urgentContacts)) return todaySummary.urgentContacts;
    return contacts.map((c) => {
      const cid = c.contact_id;
      const cDemos    = demosByContact.get(cid)    ?? [];
      const cTrials   = trialsByContact.get(cid)   ?? [];
      const cOut      = outreachByContact.get(cid) ?? [];
      const localOverride = stageOverrides[cid];
      const effectiveOverride = localOverride !== undefined ? localOverride : (c.stage_override ?? '');
      const stage = effectiveOverride || computeStage(c, cDemos, cTrials, cOut);
      if (stage === 'dead') return null;
      const { score, signals, borderColor } = computeUrgency(c, cDemos, cTrials, cOut, settings);
      if (score === 0) return null;
      const firm = firmMap[c.firm_id] ?? {};
      const lastTouchMs = cOut.map((r) => new Date(r.date || 0).getTime()).filter((t) => !isNaN(t) && t > 0).reduce((max, t) => Math.max(max, t), 0);
      return { ...c, _stage: stage, _firmName: firm.firm_name ?? '', _urgency: score, _signals: signals, _borderColor: borderColor, _lastTouchDate: lastTouchMs > 0 ? new Date(lastTouchMs).toISOString() : '' };
    }).filter(Boolean).sort((a, b) => b._urgency - a._urgency);
  }, [todaySummary, contacts, firms, demos, trials, firmMap, stageOverrides, outreachByContact, demosByContact, trialsByContact, settings]);

  const activeTrials = useMemo(() => {
    if (Array.isArray(todaySummary?.activeTrials)) return todaySummary.activeTrials;
    return trials.filter((t) => String(t.status ?? '').toLowerCase() === 'active').map((t) => {
      const contact = contactById[t.contact_id] ?? {};
      const firm = firmMap[t.firm_id] ?? firmMap[contact.firm_id] ?? {};
      const health = computeHealth(t, settings);
      const daysLeft = t.end_date ? Math.ceil((new Date(t.end_date).getTime() - Date.now()) / 86_400_000) : null;
      const cOut = outreachByContact.get(t.contact_id) ?? [];
      const lastTouchMs = cOut.map((r) => new Date(r.date || 0).getTime()).filter((v) => !isNaN(v) && v > 0).reduce((max, v) => Math.max(max, v), 0);
      return { trial: t, contact, firm, health, daysLeft, lastTouchDate: lastTouchMs ? new Date(lastTouchMs).toISOString() : '' };
    });
  }, [todaySummary, trials, contactById, firmMap, outreachByContact, settings]);

  // todayStr must be declared before any useMemo that uses it in deps
  const todayStr = new Date().toLocaleDateString('sv');  // YYYY-MM-DD in local timezone

  const trialMissions = useMemo(() => {
    return [...activeTrials]
      .filter(({ trial }) => {
        if (dismissedTrials.has(trial.trial_id)) return false;
        const snoozeUntil = snoozedTrials[trial.trial_id];
        if (snoozeUntil && snoozeUntil > todayStr) return false;
        return true;
      })
      .sort((a, b) => {
        const ua = a.health.score === 'red' ? 2 : a.health.score === 'amber' ? 1 : 0;
        const ub = b.health.score === 'red' ? 2 : b.health.score === 'amber' ? 1 : 0;
        if (ua !== ub) return ub - ua;
        return (a.daysLeft ?? 999) - (b.daysLeft ?? 999);
      });
  }, [activeTrials, dismissedTrials, snoozedTrials, todayStr]);

  const isScheduledPast = useCallback((scheduledDate) => {
    if (!scheduledDate) return false;
    if (scheduledDate.includes('T')) return new Date(scheduledDate).getTime() < Date.now();
    const d = new Date(scheduledDate);
    const dayMs = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return dayMs < new Date(new Date().setHours(0, 0, 0, 0)).getTime();
  }, []);

  const todayDemoRows = useMemo(() => {
    if (Array.isArray(todaySummary?.todayDemoRows)) return todaySummary.todayDemoRows;
    return demos
      .filter((d) => (d.status ?? '').toLowerCase() === 'scheduled'
        && (d.scheduled_date ?? '').slice(0, 10) === todayStr
        && !isScheduledPast(d.scheduled_date)
        && !dismissedTodayDemos.has(d.demo_id)
        && !(snoozedTodayDemos[d.demo_id] > todayStr))
      .map((d) => {
        const contact = contactById[d.contact_id] ?? {};
        const firm = firmMap[d.firm_id] ?? firmMap[contact.firm_id] ?? {};
        return { demo: d, contact, firm };
      })
      .sort((a, b) => String(a.demo.scheduled_date ?? '').localeCompare(String(b.demo.scheduled_date ?? '')));
  }, [demos, contactById, firmMap, todayStr, isScheduledPast, dismissedTodayDemos, snoozedTodayDemos]);

  const needsOutcomeDemos = useMemo(() => {
    if (Array.isArray(todaySummary?.needsOutcomeDemos)) return todaySummary.needsOutcomeDemos;
    return demos.filter((d) => {
      const st = (d.status ?? '').toLowerCase();
      if ((d.next_step ?? '').toLowerCase() === 'dead') return false;
      if (dismissedNeedsOutcome.has(d.demo_id)) return false;
      if (snoozedNeedsOutcome[d.demo_id] > todayStr) return false;
      if (st === 'completed' && !String(d.outcome ?? '').trim() && (!d.scheduled_date || isScheduledPast(d.scheduled_date))) return true;
      if (st === 'scheduled') return isScheduledPast(d.scheduled_date);
      return false;
    }).map((d) => {
      const contact = contactById[d.contact_id] ?? {};
      const firm = firmMap[d.firm_id] ?? firmMap[contact.firm_id] ?? {};
      return { demo: d, contact, firm };
    }).sort((a, b) => String(b.demo.scheduled_date ?? '').localeCompare(String(a.demo.scheduled_date ?? '')));
  }, [demos, contactById, firmMap, isScheduledPast, dismissedNeedsOutcome, snoozedNeedsOutcome, todayStr]);

  const overdueMissions = useMemo(() => {
    if (Array.isArray(todaySummary?.overdueMissions)) return todaySummary.overdueMissions;
    return demos.filter((d) => {
      if ((d.status ?? '').toLowerCase() === 'cancelled') return false;
      if (!(d.next_step ?? '').trim()) return false;
      const due = (d.next_step_due ?? '').slice(0, 10);
      if (!due || due >= todayStr) return false;
      if (dismissedOverdueNextSteps.has(d.demo_id)) return false;
      if (snoozedOverdueNextSteps[d.demo_id] > todayStr) return false;
      return true;
    }).map((d) => {
      const contact = contactById[d.contact_id] ?? {};
      const firm = firmMap[d.firm_id] ?? firmMap[contact.firm_id] ?? {};
      const daysOverdue = Math.max(1, Math.floor((Date.now() - new Date(d.next_step_due).getTime()) / 86_400_000));
      return { demo: d, contact, firm, daysOverdue };
    }).sort((a, b) => b.daysOverdue - a.daysOverdue);
  }, [demos, contactById, firmMap, todayStr, dismissedOverdueNextSteps, snoozedOverdueNextSteps]);

  const nextStepMissions = useMemo(() => {
    return demos.filter((d) => {
      if ((d.status ?? '').toLowerCase() === 'cancelled') return false;
      if (!(d.next_step ?? '').trim()) return false;
      const due = (d.next_step_due ?? '').slice(0, 10);
      // Only show if due today or future (or no due date); overdue handled by overdueMissions
      if (due && due < todayStr) return false;
      if (dismissedNextSteps.has(d.demo_id)) return false;
      if (snoozedNextSteps[d.demo_id] > todayStr) return false;
      return true;
    }).map((d) => {
      const contact = contactById[d.contact_id] ?? {};
      const firm = firmMap[d.firm_id] ?? firmMap[contact.firm_id] ?? {};
      const due = (d.next_step_due ?? '').slice(0, 10);
      const daysUntil = due
        ? Math.ceil((new Date(due + 'T00:00:00').getTime() - new Date(todayStr + 'T00:00:00').getTime()) / 86_400_000)
        : null;
      return { demo: d, contact, firm, daysUntil };
    }).sort((a, b) => {
      const aD = a.demo.next_step_due ?? '9999';
      const bD = b.demo.next_step_due ?? '9999';
      return aD.localeCompare(bD);
    });
  }, [demos, contactById, firmMap, todayStr, dismissedNextSteps, snoozedNextSteps]);

  const outreachMissions = useMemo(() => {
    if (Array.isArray(todaySummary?.outreachMissions)) return todaySummary.outreachMissions;
    return urgentContacts.filter((c) => c._stage === 'cold' || c._stage === 'warm' || c._stage === 'cold-untouched').slice(0, 4);
  }, [urgentContacts]);

  const resolveContact = (r) => {
    const byId = r.contact_id ? (contactById[r.contact_id] ?? null) : null;
    return byId ?? {};
  };

  const followupItems = useMemo(() => {
    if (Array.isArray(todaySummary?.followupItems)) return todaySummary.followupItems;
    const unsubKeys = new Set(
      effectiveOutreachLog
        .filter((r) => r.reply_type === 'unsubscribe')
        .flatMap((r) => [r.contact_id, r.gmail_thread_id].filter(Boolean))
    );
    const seen = new Set();
    return effectiveOutreachLog
      .filter((r) => r.reply_type === 'need_followup')
      .filter((r) => !unsubKeys.has(r.contact_id) && !unsubKeys.has(r.gmail_thread_id))
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .filter((r) => {
        const key = r.contact_id || r.gmail_thread_id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .filter((r) => {
        const key = r.contact_id || r.gmail_thread_id;
        return !(snoozedFollowupIncoming[key] > todayStr);
      })
      .map((r) => {
        const contact = resolveContact(r);
        const firm = firmMap[contact.firm_id] ?? {};
        return { log: r, contact, firm };
      });
  }, [effectiveOutreachLog, contactById, firmMap, snoozedFollowupIncoming, todayStr]);

  const sortedReminders = useMemo(() => {
    return Object.entries(batchReminders)
      .map(([key, due]) => ({ key, due }))
      .sort((a, b) => a.due.localeCompare(b.due));
  }, [batchReminders]);

  const activeBdSuggestions = useMemo(() => {
    return bdSuggestions.filter((s) => {
      const key = s.contact_id || s.email;
      if (!key) return false;
      if (dismissedBdSuggestions.has(key)) return false;
      if (repliedBdSuggestions.has(key)) return false;
      if (snoozedBdSuggestions[key] > todayStr) return false;
      return true;
    });
  }, [bdSuggestions, dismissedBdSuggestions, repliedBdSuggestions, snoozedBdSuggestions, todayStr]);

  const outreachBatches = useMemo(() => {
    if (Array.isArray(todaySummary?.outreachBatches)) return todaySummary.outreachBatches;
    const batchMap = new Map();
    for (const r of effectiveOutreachLog) {
      if ((r.direction ?? '').toLowerCase() !== 'outbound') continue;
      const subject = (r.subject ?? '').trim();
      const day = (r.date ?? '').slice(0, 10);
      if (!subject || !day) continue;
      const key = `${day}||${subject}`;
      const batch = batchMap.get(key) ?? { key, subject, day, rows: [] };
      batch.rows.push(r);
      batchMap.set(key, batch);
    }

    const byDay = new Map();
    for (const batch of batchMap.values()) {
      const arr = byDay.get(batch.day) ?? [];
      arr.push(batch);
      byDay.set(batch.day, arr);
    }

    const merged = [];
    for (const day of [...byDay.keys()].sort((a, b) => b.localeCompare(a))) {
      const batches = byDay.get(day) ?? [];
      for (const batch of batches) {
        let targetIndex = -1;
        for (let i = 0; i < merged.length; i++) {
          const m = merged[i];
          if (m.day !== batch.day) continue;
          if (subjectSimilarity(m.subject, batch.subject) >= 0.6) {
            targetIndex = i;
            break;
          }
        }
        if (targetIndex >= 0) {
          const existing = merged[targetIndex];
          existing.rows = existing.rows.concat(batch.rows);
          if (batch.subject.length > existing.subject.length) existing.subject = batch.subject;
        } else {
          merged.push({ ...batch });
        }
      }
    }

    const bdBatchMin = Number(settings?.bdBatchMin ?? 3);
    return merged.filter((b) => b.rows.length >= bdBatchMin && !batchReminders[b.key] && !dismissedBatches.has(b.key)).slice(0, 8);
  }, [todaySummary, effectiveOutreachLog, batchReminders, dismissedBatches]);

  // Match each outreach batch to a BD campaign upload by (date, subject) similarity
  const batchUploadMap = useMemo(() => {
    const map = new Map(); // batch.key → upload_id
    const DATE_WINDOW_DAYS = 3; // upload day and send day often differ by a day or two
    const dayDiff = (a, b) => {
      const da = new Date(a), db = new Date(b);
      if (isNaN(da) || isNaN(db)) return Infinity;
      return Math.abs(da - db) / 86_400_000;
    };
    for (const batch of outreachBatches) {
      // Prefer the closest-dated campaign that clears the subject-similarity bar.
      let best = null, bestScore = 0, bestDelta = Infinity;
      for (const stat of bdCampaignStats) {
        const delta = dayDiff(stat.campaign_date, batch.day);
        if (delta > DATE_WINDOW_DAYS) continue;
        const score = subjectSimilarity(batch.subject, stat.subject);
        if (score < 0.5) continue;
        if (delta < bestDelta || (delta === bestDelta && score > bestScore)) {
          bestDelta = delta; bestScore = score; best = stat;
        }
      }
      if (best) map.set(batch.key, best.upload_id);
    }
    return map;
  }, [outreachBatches, bdCampaignStats]);

  // Delivered count from BD stats (falls back to outreach_log row count)
  const batchDeliveredCount = useMemo(() => {
    const map = new Map();
    for (const batch of outreachBatches) {
      const uploadId = batchUploadMap.get(batch.key);
      if (uploadId) {
        const stat = bdCampaignStats.find((s) => s.upload_id === uploadId);
        map.set(batch.key, stat?.total_delivered ?? batch.rows.length);
      } else {
        map.set(batch.key, batch.rows.length);
      }
    }
    return map;
  }, [outreachBatches, batchUploadMap, bdCampaignStats]);

  // Fallback when no BigDough upload matched: build the list from the outreach
  // log. We only know who was contacted and who replied — no open/click signal —
  // so everyone lands in either 'replied' or 'no_engagement'.
  function buildFollowupFromLog(batch) {
    const repliedContactIds = new Set(
      effectiveOutreachLog
        .filter((r) => (r.direction ?? '').toLowerCase() === 'inbound' && r.reply_type === 'need_followup')
        .map((r) => r.contact_id)
        .filter(Boolean)
    );
    const seen = new Set();
    const out = [];
    for (const r of batch.rows) {
      const contact = resolveContact(r);
      const email = (contact.email || r.email || '').toLowerCase();
      const key = r.contact_id || email;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const firm = firmMap[contact.firm_id] ?? {};
      out.push({
        email: contact.email || r.email || '',
        name: contact.name || r.name || '',
        firm_name: firm.firm_name || r.institution || '',
        category: (r.contact_id && repliedContactIds.has(r.contact_id)) ? 'replied' : 'no_engagement',
      });
    }
    return out;
  }

  async function handleExportFollowup(batch, uploadId, categories) {
    setFollowupLoading(true);
    try {
      const rows = uploadId
        ? await fetchBdFollowupList(getToken, uploadId)
        : buildFollowupFromLog(batch);
      const LABEL = {
        high_intent: 'High Intent', opened: 'Opened',
        no_engagement: 'No Engagement', replied: 'Replied',
      };
      const filtered = rows.filter((r) => categories[r.category]);
      const sheetData = [
        ['Name', 'Email', 'Company', 'Category'],
        ...filtered.map((r) => [
          r.name || '', r.email || '', r.firm_name || '', LABEL[r.category] || r.category,
        ]),
      ];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(wb, ws, 'Followup List');
      XLSX.writeFile(wb, `followup_${batch.day}_${batch.subject.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}.xlsx`);
      setFollowupModal(null);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setFollowupLoading(false);
    }
  }

  // All hooks above — guard must come after them to satisfy Rules of Hooks.
  if (!data) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <span>Loading today's view…</span>
      </div>
    );
  }

  // Gamification
  const xpTotal = xpBump?.xp_total != null ? xpBump.xp_total : (parseInt(config['xp_total'] ?? '0') || 0);
  const streak = xpBump?.streak_days != null ? xpBump.streak_days : (parseInt(config['streak_days'] ?? '0') || 0);
  const dailyTarget = parseInt(settings?.dailyTarget ?? config['daily_target'] ?? '4', 10) || 4;

  const weekStart = startOfWeek();
  const todayOutbound = effectiveOutreachLog.filter((r) => (r.direction ?? '').toLowerCase() === 'outbound' && (r.date ?? '').slice(0, 10) === todayStr).length;
  const weekOutbound = effectiveOutreachLog.filter((r) => {
    if ((r.direction ?? '').toLowerCase() !== 'outbound') return false;
    const d = new Date(r.date || 0);
    return !isNaN(d) && d >= weekStart;
  }).length;

  const countReplyTypeUnique = (type, sinceDate) => {
    const seen = new Set();
    for (const r of effectiveOutreachLog) {
      if (r.reply_type !== type) continue;
      const d = new Date(r.date || 0);
      if (isNaN(d) || d < sinceDate) continue;
      seen.add(r.contact_id || r.gmail_thread_id);
    }
    return seen.size;
  };

  const todayDate = new Date(todayStr + 'T00:00:00');
  const statsToday = todaySummary?.statsToday ?? {
    sent: todayOutbound,
    unsub: countReplyTypeUnique('unsubscribe', todayDate),
    bounced: countReplyTypeUnique('bounce', todayDate),
    followup: followupItems.length,
  };
  const statsWtd = todaySummary?.statsWtd ?? {
    sent: weekOutbound,
    unsub: countReplyTypeUnique('unsubscribe', weekStart),
    bounced: countReplyTypeUnique('bounce', weekStart),
    followup: followupItems.length,
  };

  const lastBD = config['last_bigdough_sync'] ?? '';
  const showBdWarning = new Date().getHours() >= 9 && !(lastBD && lastBD.slice(0, 10) === todayStr);

  const urgentHighCount = urgentContacts.filter((c) => c._urgency >= 30).length;
  const todayMissionCount = todaySummary?.todayMissionCount ?? Math.min(dailyTarget, todayOutbound + urgentHighCount + todayDemoRows.length + activeTrials.length);
  const todayProgress = todaySummary?.todayProgress ?? Math.min(100, (todayMissionCount / Math.max(dailyTarget, 1)) * 100);
  const level = Math.max(1, Math.floor(xpTotal / 40) || 1);
  const xpIntoLevel = xpTotal % 40;
  const greeting = new Date().getHours() < 12 ? 'Good morning' : 'Good afternoon';
  const greetingName = displayNameFrom(userEmail, userName);
  const demoMissionCount = todayDemoRows.length + needsOutcomeDemos.length + overdueMissions.length + nextStepMissions.length;

  function openQuickLog(contact, firmName) {
    if (!getToken) return;
    setQuickLogTarget({ contact, firmName: firmName ?? '' });
  }

  function handleLogSaved(gamification) {
    setQuickLogTarget(null);
    if (gamification) setXpBump(gamification);
    onRefreshOutreach?.();
    onRefreshAll?.();
  }

  function dismissAddContactBanner(demoId) {
    const next = addContactBanners.filter((b) => b.demo_id !== demoId);
    setAddContactBanners(next);
    localStorage.setItem('addContactBanners', JSON.stringify(next));
  }

  function dismissBatch(key) {
    const next = new Set([...dismissedBatches, key]);
    setDismissedBatches(next);
    localStorage.setItem('dismissedBatches', JSON.stringify([...next]));
  }

  function saveBatchReminder(key) {
    const due = reminderInput[key] ?? '';
    if (!due || due <= todayStr) return;
    const next = { ...batchReminders, [key]: due };
    setBatchReminders(next);
    localStorage.setItem('batchReminders', JSON.stringify(next));
    dismissBatch(key);
  }

  function clearBatchReminder(key) {
    const next = { ...batchReminders };
    delete next[key];
    setBatchReminders(next);
    localStorage.setItem('batchReminders', JSON.stringify(next));
    dismissBatch(key);
  }

  function openDemoOutcome(demo, contact, firm) {
    if (!getToken) return;
    setDemoOutcomeTarget({ demo, contact, firm });
  }

  function handleDemoOutcomeSaved() {
    setDemoOutcomeTarget(null);
    setCreateTrialTarget(null);
    onRefreshOutreach?.();
    onRefreshAll?.();
  }

  // ── Snooze / dismiss helpers ────────────────────────────────────────────────

  function snoozeSetMap(setter, storageKey, id, date) {
    setter((prev) => {
      const n = { ...prev, [id]: date };
      localStorage.setItem(storageKey, JSON.stringify(n));
      return n;
    });
    setRemindPickerKey(null);
    setRemindPickerDate('');
  }

  function dismissSetSet(setter, storageKey, id) {
    setter((prev) => {
      const n = new Set([...prev, id]);
      localStorage.setItem(storageKey, JSON.stringify([...n]));
      return n;
    });
  }

  const snoozeTrial = (id, date) => snoozeSetMap(setSnoozedTrials, 'snoozedTrials', id, date);
  const dismissTrial = (id) => dismissSetSet(setDismissedTrials, 'dismissedTrials', id);
  const snoozeTodayDemo = (id, date) => snoozeSetMap(setSnoozedTodayDemos, 'snoozedTodayDemos', id, date);
  const dismissTodayDemo = (id) => dismissSetSet(setDismissedTodayDemos, 'dismissedTodayDemos', id);
  const snoozeNeedsOutcomeFn = (id, date) => snoozeSetMap(setSnoozedNeedsOutcome, 'snoozedNeedsOutcome', id, date);
  const dismissNeedsOutcomeFn = (id) => dismissSetSet(setDismissedNeedsOutcome, 'dismissedNeedsOutcome', id);
  const snoozeOverdueNextStep = (id, date) => snoozeSetMap(setSnoozedOverdueNextSteps, 'snoozedOverdueNextSteps', id, date);
  const dismissOverdueNextStep = (id) => dismissSetSet(setDismissedOverdueNextSteps, 'dismissedOverdueNextSteps', id);
  const snoozeNextStepFn = (id, date) => snoozeSetMap(setSnoozedNextSteps, 'snoozedNextSteps', id, date);
  const dismissNextStepFn = (id) => dismissSetSet(setDismissedNextSteps, 'dismissedNextSteps', id);

  function snoozeFollowupIncomingFn(key, date) {
    setSnoozedFollowupIncoming((prev) => {
      const n = { ...prev, [key]: date };
      localStorage.setItem('snoozedFollowupIncoming', JSON.stringify(n));
      return n;
    });
  }

  function snoozeBdSuggestionFn(key, date) {
    setSnoozedBdSuggestions((prev) => {
      const n = { ...prev, [key]: date };
      localStorage.setItem('snoozedBdSuggestions', JSON.stringify(n));
      return n;
    });
  }

  function dismissBdSuggestionFn(key) {
    setDismissedBdSuggestions((prev) => {
      const n = new Set([...prev, key]);
      localStorage.setItem('dismissedBdSuggestions', JSON.stringify([...n]));
      return n;
    });
  }

  function repliedBdSuggestionFn(key, suggestion) {
    setRepliedBdSuggestions((prev) => {
      const n = new Set([...prev, key]);
      localStorage.setItem('repliedBdSuggestions', JSON.stringify([...n]));
      return n;
    });
    if (getToken && suggestion?.contact_id) {
      appendOutreachLog(getToken, {
        contact_id: suggestion.contact_id,
        firm_id: suggestion.firm_id ?? '',
        date: todayStr,
        channel: 'Email',
        direction: 'Outbound',
        type: 'Email',
        notes: `BD follow-up (replied via suggestion)`,
        reply_type: 'replied',
      }).then(() => {
        onRefreshOutreach?.();
        onRefreshAll?.();
      }).catch(() => {});
    }
  }

  async function markNextStepDone(demoId) {
    if (!getToken) return;
    setNextStepDoneLoading(demoId);
    try {
      await updateDemo(getToken, demoId, { next_step: '', next_step_due: '' });
      dismissNextStepFn(demoId);
      onRefreshOutreach?.();
      onRefreshAll?.();
    } catch (e) {
      console.error('markNextStepDone failed:', e);
    } finally {
      setNextStepDoneLoading(null);
    }
  }

  // Remind Later picker dispatch
  function openRemindPicker(key) {
    setRemindPickerKey(key);
    setRemindPickerDate('');
  }

  function confirmRemindLater(key, date) {
    if (!date) return;
    const colonIdx = key.indexOf(':');
    const type = key.slice(0, colonIdx);
    const id = key.slice(colonIdx + 1);
    if (type === 'trial') snoozeTrial(id, date);
    else if (type === 'todaydemo') snoozeTodayDemo(id, date);
    else if (type === 'needsoutcome') snoozeNeedsOutcomeFn(id, date);
    else if (type === 'overduestep') snoozeOverdueNextStep(id, date);
    else if (type === 'nextstep') snoozeNextStepFn(id, date);
    setRemindPickerKey(null);
    setRemindPickerDate('');
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* System-status banners only */}
      {showBdWarning && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--amber-bg)', border: '1px solid rgba(217,119,6,.3)', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: 16 }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚠</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>BigDough not synced today</div>
            <div style={{ fontSize: 12, color: '#b45309' }}>{lastBD ? `Last sync: ${relDate(lastBD)}` : "No sync on record — upload this morning's export now."}</div>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={() => onTabChange?.('sync')}>Upload now</button>
        </div>
      )}

      {pendingBdCandidates.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--amber-bg)', border: '1px solid rgba(217,119,6,.3)', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: 16 }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>👥</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
              {pendingBdCandidates.length} BigDough contact{pendingBdCandidates.length === 1 ? '' : 's'} waiting for review
            </div>
            <div style={{ fontSize: 12, color: '#b45309' }}>
              Not yet approved — their outreach touches don't show up anywhere else until you review them.
              {pendingBdCandidates[pendingBdCandidates.length - 1]?.created_at
                ? ` Oldest: ${relDate(pendingBdCandidates[pendingBdCandidates.length - 1].created_at)}.`
                : ''}
            </div>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={() => onTabChange?.('sync')}>Review now</button>
        </div>
      )}

      {addContactBanners.map((b) => (
        <div key={b.demo_id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: 12 }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>👤</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>Add contacts from {b.contact_name || 'demo'}{b.demo_title ? ` — "${b.demo_title}"` : ''}</div>
            <div style={{ fontSize: 12, color: '#3b82f6' }}>Were there attendees at this demo who aren't in your contact list?</div>
          </div>
          <button className="btn btn--sm" style={{ background: '#3b82f6', color: '#fff', border: 'none' }} onClick={() => onTabChange?.('inprogress')}>Go to Demos</button>
          <button className="btn btn--ghost btn--sm" onClick={() => dismissAddContactBanner(b.demo_id)} style={{ color: 'var(--text-3)' }}>✕</button>
        </div>
      ))}

      {/* Win condition + Level */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '28px 28px 24px', display: 'grid', gridTemplateColumns: '1fr 1px 220px', gap: 0, marginBottom: 28 }}>
        <div style={{ paddingRight: 24 }}>
          <div style={{ fontSize: 22, lineHeight: 1.12, fontWeight: 800, color: 'var(--navy)', marginBottom: 8, letterSpacing: '-0.02em' }}>
            {greeting}{greetingName ? `, ${greetingName}` : ''} 👋
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--navy)', marginBottom: 16 }}>Today's Win Condition</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            <LaneChip tone="purple">{trialMissions.length} Trial</LaneChip>
            <LaneChip tone="green">{todayDemoRows.length} Demo</LaneChip>
            {followupItems.length > 0 && <LaneChip tone="amber">{followupItems.length} Follow Up</LaneChip>}
            {overdueMissions.length > 0 && <LaneChip tone="red">{overdueMissions.length} Overdue</LaneChip>}
          </div>
          <div className="progress-row">
            <div style={{ flex: 1, height: 7, background: 'var(--border)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${todayProgress}%`, borderRadius: 999, background: 'linear-gradient(90deg,var(--purple),var(--blue))', transition: 'width .4s' }} />
            </div>
            <span className="progress-label">{todayMissionCount} / {dailyTarget} done</span>
          </div>
        </div>
        <div style={{ background: 'var(--border)', margin: '0 24px' }} />
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 8 }}>Your level</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--orange)', color: '#fff', padding: '5px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700 }}>★ Level {level}</div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 18, lineHeight: 1 }}>🔥</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber)', marginTop: 3 }}>{streak}d streak</div>
            </div>
          </div>
          <div style={{ height: 6, background: 'var(--border)', borderRadius: 999, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ height: '100%', width: `${(xpIntoLevel / 40) * 100}%`, background: 'linear-gradient(90deg,var(--purple),var(--blue))', borderRadius: 999 }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)' }}>
            <span>{xpIntoLevel} XP</span><span>40 to next</span>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      {(() => {
        const s = statsPeriod === 'today' ? statsToday : statsWtd;
        return (
          <div className="stats-strip">
            <div className="stats-toggle">
              <button className={`stats-toggle-btn${statsPeriod === 'today' ? ' active' : ''}`} onClick={() => setStatsPeriod('today')}>Today</button>
              <button className={`stats-toggle-btn${statsPeriod === 'wtd' ? ' active' : ''}`} onClick={() => setStatsPeriod('wtd')}>WTD</button>
            </div>
            <span className="stats-group-label">Outbound</span>
            <div className="stats-items">
              <div className="stat-item">
                <div className={`stat-val ${s.sent > 0 ? 'stat-val--orange' : 'stat-val--muted'}`}>{s.sent}</div>
                <div className="stat-lbl">Emails Sent</div>
              </div>
            </div>
            <div className="stats-divider" />
            <span className="stats-group-label">Inbound</span>
            <div className="stats-items">
              <div className="stat-item">
                <div className={`stat-val ${s.unsub > 0 ? '' : 'stat-val--muted'}`}>{s.unsub}</div>
                <div className="stat-lbl">Unsubscribed</div>
              </div>
              <div className="stat-item">
                <div className={`stat-val ${s.bounced > 0 ? 'stat-val--amber' : 'stat-val--muted'}`}>{s.bounced}</div>
                <div className="stat-lbl">Bounced</div>
              </div>
              <div className="stat-item stat-item--clickable" title="Jump to Follow Up missions"
                onClick={() => document.querySelector('.lane--followup')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}>
                <div className={`stat-val ${s.followup > 0 ? 'stat-val--red' : 'stat-val--muted'}`}>{s.followup}</div>
                <div className={`stat-lbl ${s.followup > 0 ? 'stat-lbl--red' : ''}`}>Need Followup ↗</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* GTM Weekly Progress */}
      {(() => {
        const gtm = todaySummary?.gtmWeekProgress;
        const gtmTarget = parseInt(config?.gtm_weekly_target ?? '2500', 10) || 2500;
        const weekSent = gtm?.sent ?? statsWtd.sent;
        const pct = Math.min(100, Math.round((weekSent / gtmTarget) * 100));
        const positiveReplies = todaySummary?.weekPositiveReplies ?? 0;
        async function runClassify() {
          if (!getToken || classifying) return;
          setClassifying(true);
          setClassifyMsg('');
          try {
            const r = await classifyReplies(getToken);
            setClassifyMsg(`✓ Classified ${r.classified} replies`);
            onRefreshOutreach?.();
          } catch (e) {
            setClassifyMsg(`Error: ${e.message}`);
          } finally {
            setClassifying(false);
          }
        }
        return (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2,var(--text-muted))' }}>GTM — Weekly Outreach</span>
                <span style={{ fontSize: 12, color: pct >= 100 ? 'var(--green,#22c55e)' : 'var(--text-3,var(--text-muted))' }}>{weekSent.toLocaleString()} / {gtmTarget.toLocaleString()}</span>
              </div>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? 'var(--green,#22c55e)' : 'linear-gradient(90deg,var(--blue,#3b82f6),var(--purple,#8b5cf6))', borderRadius: 999, transition: 'width .4s' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
              {positiveReplies > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--green,#22c55e)' }}>{positiveReplies}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-3,var(--text-muted))' }}>Positive replies</div>
                </div>
              )}
              <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={() => onTabChange?.('outreach')}>Outreach Queue →</button>
              <button className="btn btn--ghost btn--sm" style={{ fontSize: 11, opacity: classifying ? 0.6 : 1 }} disabled={classifying} onClick={runClassify}>
                {classifying ? 'Classifying…' : '✨ Classify Replies'}
              </button>
              {classifyMsg && <span style={{ fontSize: 11, color: classifyMsg.startsWith('Error') ? 'var(--red,#ef4444)' : 'var(--green,#22c55e)' }}>{classifyMsg}</span>}
            </div>
          </div>
        );
      })()}

      {/* Smart Missions header */}
      <div className="section-header">
        <div className="section-title">
          <div className="section-title-bar" />
          Smart Missions
        </div>
      </div>

      <div className="board">

        {/* ── Trial Management ── */}
        <div className="lane">
          <div className="lane-header">
            <div className="lane-header__title">Trial Management</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <LaneChip tone="gray">{laneSummary(trialMissions.length, trialMissions.length * 70)}</LaneChip>
              {trialMissions.some((t) => t.health.score === 'red') && <LaneChip tone="red">high risk</LaneChip>}
            </div>
          </div>
          <div className="lane-body">
            {trialMissions.length === 0 ? (
              <div className="empty-lane"><div className="empty-lane__icon">🧪</div><div>No active trials</div></div>
            ) : (
              trialMissions.slice(0, 3).map(({ trial, contact, firm, health, daysLeft, lastTouchDate }) => {
                const tone = health.score === 'red' ? 'red' : health.score === 'amber' ? 'amber' : 'gray';
                const name = getContactName(contact, firm?.firm_name);
                const subtitle = [firm?.firm_name, contact?.title].filter(Boolean).join(' · ') || 'Active trial';
                const pickerKey = `trial:${trial.trial_id}`;
                const liveEvents = parseInt(trial.live_events_this_week ?? '0', 10) || 0;
                const workflows  = parseInt(trial.preset_workflows_this_week ?? '0', 10) || 0;
                const usageMode  = liveEvents === 0 && workflows === 0 ? 'Inactive'
                                 : workflows === 0 ? 'Chat only'
                                 : 'Full workflow';
                const usageTone  = usageMode === 'Inactive' ? '#ef4444' : usageMode === 'Chat only' ? '#f59e0b' : '#22c55e';
                const riskScore  = tone === 'red' ? 3 : tone === 'amber' ? 2 : 1;
                const riskLabel  = riskScore === 3 ? '🔴 High risk' : riskScore === 2 ? '🟡 Medium risk' : '🟢 On track';
                return (
                  <MissionCard key={trial.trial_id} tone={tone} title={name} subtitle={subtitle}
                    xp={`+${tone === 'red' ? 90 : tone === 'amber' ? 60 : 45} XP`}
                    signals={[
                      daysLeft != null && `Trial ends in ${Math.max(0, daysLeft)}d`,
                      lastTouchDate && `Last touch ${relDate(lastTouchDate)}`,
                      <span key="usage" style={{ color: usageTone, fontWeight: 600 }}>{usageMode}</span>,
                      riskLabel,
                    ].filter(Boolean)}
                    ai={tone === 'red' ? 'Re-engagement draft ready' : tone === 'amber' ? 'Urgency follow-up draft ready' : 'Reply-to-click email ready'}
                    actions={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button className="btn btn--primary btn--sm" onClick={() => openQuickLog(contact, firm?.firm_name)}>Auto-log</button>
                          <button className="btn btn--ghost btn--sm" onClick={() => onNavigateToTrial?.(trial.trial_id)}>View →</button>
                          <button className="btn btn--ghost btn--sm" onClick={() => openRemindPicker(pickerKey)}>⏰ Remind Later</button>
                          <button className="btn btn--ghost btn--sm" style={{ color: 'var(--text-3)' }} onClick={() => dismissTrial(trial.trial_id)}>Dismiss</button>
                        </div>
                        {remindPickerKey === pickerKey && (
                          <RemindLaterPicker
                            todayStr={todayStr}
                            onConfirm={(date) => confirmRemindLater(pickerKey, date)}
                            onCancel={() => setRemindPickerKey(null)}
                          />
                        )}
                      </div>
                    }
                  />
                );
              })
            )}
          </div>
        </div>

        {/* ── Follow Up ── */}
        <FollowUpLane
          followupItems={followupItems}
          sortedReminders={sortedReminders}
          todayStr={todayStr}
          onOpenTimeline={onOpenTimeline}
          clearBatchReminder={clearBatchReminder}
          onOverrideReplyType={handleOverrideReplyType}
          activeBdSuggestions={activeBdSuggestions}
          onSnoozeFollowupIncoming={snoozeFollowupIncomingFn}
          onSnoozeBdSuggestion={snoozeBdSuggestionFn}
          onDismissBdSuggestion={dismissBdSuggestionFn}
          onRepliedBdSuggestion={repliedBdSuggestionFn}
        />

        {/* ── Demo Management ── */}
        <div className="lane">
          <div className="lane-header">
            <div className="lane-header__title">Demo Management</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <LaneChip tone="gray">{laneSummary(demoMissionCount, demoMissionCount * 50)}</LaneChip>
              {todayDemoRows.length > 0 && <LaneChip tone="amber">Today</LaneChip>}
            </div>
          </div>
          <div className="lane-body">
            {demoMissionCount === 0 ? (
              <div className="empty-lane"><div className="empty-lane__icon">🎯</div><div>No demo missions</div></div>
            ) : (
              <>
                {/* Today's demos */}
                {todayDemoRows.length > 0 && (
                  <>
                    <div style={LABEL_STYLE}>Today</div>
                    {todayDemoRows.map(({ demo, contact, firm }) => {
                      const eventTitle = demo.event_title || demo.title || getContactName(contact, firm?.firm_name);
                      const attendees = resolveDemoAttendees(demo, contactByEmail, contactById);
                      const pickerKey = `todaydemo:${demo.demo_id}`;
                      return (
                        <MissionCard key={demo.demo_id} tone="amber"
                          title={eventTitle}
                          subtitle={`Demo · ${formatClock(demo.scheduled_date) || 'Today'}`}
                          xp="+50 XP"
                          signals={[
                            `Demo at ${formatClock(demo.scheduled_date) || 'today'}`,
                            attendees.length > 0 ? `👥 ${attendees.map((a) => a.name || a.email).join(', ')}` : null,
                          ].filter(Boolean)}
                          actions={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button className="btn btn--primary btn--sm" onClick={() => onOpenTimeline?.(contact)}>Prep Demo</button>
                                <button className="btn btn--ghost btn--sm" onClick={() => onNavigateToDemo?.(demo.demo_id)}>View →</button>
                                <button className="btn btn--ghost btn--sm" onClick={() => openRemindPicker(pickerKey)}>⏰ Remind Later</button>
                                <button className="btn btn--ghost btn--sm" style={{ color: 'var(--text-3)' }} onClick={() => dismissTodayDemo(demo.demo_id)}>Dismiss</button>
                              </div>
                              {remindPickerKey === pickerKey && (
                                <RemindLaterPicker
                                  todayStr={todayStr}
                                  onConfirm={(date) => confirmRemindLater(pickerKey, date)}
                                  onCancel={() => setRemindPickerKey(null)}
                                />
                              )}
                            </div>
                          }
                        />
                      );
                    })}
                  </>
                )}

                {/* Needs outcome */}
                {needsOutcomeDemos.length > 0 && (
                  <>
                    <div style={{ ...LABEL_STYLE, marginTop: todayDemoRows.length > 0 ? 16 : 0 }}>Needs Outcome</div>
                    {needsOutcomeDemos.map(({ demo, contact, firm }) => {
                      const eventTitle = demo.event_title || demo.title || getContactName(contact, firm?.firm_name);
                      const attendees = resolveDemoAttendees(demo, contactByEmail, contactById);
                      const pickerKey = `needsoutcome:${demo.demo_id}`;
                      return (
                        <MissionCard key={demo.demo_id} tone="gray"
                          title={eventTitle}
                          subtitle={['Demo completed', formatDateLabel(demo.scheduled_date)].filter(Boolean).join(' · ') || 'Demo completed'}
                          xp="+50 XP"
                          signals={[
                            'No outcome logged yet',
                            attendees.length > 0 ? `👥 ${attendees.map((a) => a.name || a.email).join(', ')}` : null,
                          ].filter(Boolean)}
                          actions={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button className="btn btn--primary btn--sm" onClick={() => openDemoOutcome(demo, contact, firm)}>Auto-log</button>
                                <button className="btn btn--ghost btn--sm" onClick={() => onNavigateToDemo?.(demo.demo_id)}>View →</button>
                                <button className="btn btn--ghost btn--sm" onClick={() => openRemindPicker(pickerKey)}>⏰ Remind Later</button>
                                <button className="btn btn--ghost btn--sm" style={{ color: 'var(--text-3)' }} onClick={() => dismissNeedsOutcomeFn(demo.demo_id)}>Dismiss</button>
                              </div>
                              {remindPickerKey === pickerKey && (
                                <RemindLaterPicker
                                  todayStr={todayStr}
                                  onConfirm={(date) => confirmRemindLater(pickerKey, date)}
                                  onCancel={() => setRemindPickerKey(null)}
                                />
                              )}
                            </div>
                          }
                        />
                      );
                    })}
                  </>
                )}

                {/* Overdue next steps */}
                {overdueMissions.length > 0 && (
                  <>
                    <div style={{ ...LABEL_STYLE, marginTop: todayDemoRows.length + needsOutcomeDemos.length > 0 ? 16 : 0 }}>Overdue Next Steps</div>
                    {overdueMissions.map(({ demo, contact, firm, daysOverdue }) => {
                      const eventTitle = demo.event_title || demo.title || getContactName(contact, firm?.firm_name);
                      const attendees = resolveDemoAttendees(demo, contactByEmail, contactById);
                      const pickerKey = `overduestep:${demo.demo_id}`;
                      return (
                        <MissionCard key={`over-${demo.demo_id}`} tone="red"
                          title={eventTitle}
                          subtitle={demo.next_step || 'Next step overdue'}
                          xp="+50 XP"
                          signals={[
                            `${daysOverdue}d overdue`,
                            demo.next_step_due ? `Was due ${formatDateLabel(demo.next_step_due)}` : null,
                            attendees.length > 0 ? `👥 ${attendees.map((a) => a.name || a.email).join(', ')}` : null,
                          ].filter(Boolean)}
                          actions={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button className="btn btn--primary btn--sm" onClick={() => onNavigateToDemo?.(demo.demo_id)}>View →</button>
                                <button className="btn btn--ghost btn--sm" onClick={() => openRemindPicker(pickerKey)}>⏰ Remind Later</button>
                                <button className="btn btn--ghost btn--sm" style={{ color: 'var(--text-3)' }} onClick={() => dismissOverdueNextStep(demo.demo_id)}>Dismiss</button>
                              </div>
                              {remindPickerKey === pickerKey && (
                                <RemindLaterPicker
                                  todayStr={todayStr}
                                  onConfirm={(date) => confirmRemindLater(pickerKey, date)}
                                  onCancel={() => setRemindPickerKey(null)}
                                />
                              )}
                            </div>
                          }
                        />
                      );
                    })}
                  </>
                )}

                {/* Next Step Follow-up */}
                {nextStepMissions.length > 0 && (
                  <>
                    <div style={{ ...LABEL_STYLE, marginTop: todayDemoRows.length + needsOutcomeDemos.length + overdueMissions.length > 0 ? 16 : 0 }}>Next Step Follow-up</div>
                    {nextStepMissions.map(({ demo, contact, firm, daysUntil }) => {
                      const eventTitle = demo.event_title || demo.title || getContactName(contact, firm?.firm_name);
                      const pickerKey = `nextstep:${demo.demo_id}`;
                      const dueLabel = daysUntil === null ? null : daysUntil === 0 ? 'Due today' : daysUntil === 1 ? 'Due tomorrow' : `Due in ${daysUntil}d`;
                      return (
                        <MissionCard key={`ns-${demo.demo_id}`} tone="amber"
                          title={eventTitle}
                          subtitle={demo.next_step || 'Next step scheduled'}
                          xp="+50 XP"
                          signals={[
                            dueLabel,
                            demo.next_step_due ? `Due ${formatDateLabel(demo.next_step_due)}` : null,
                          ].filter(Boolean)}
                          actions={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button className="btn btn--primary btn--sm"
                                  disabled={nextStepDoneLoading === demo.demo_id}
                                  onClick={() => markNextStepDone(demo.demo_id)}>
                                  {nextStepDoneLoading === demo.demo_id ? 'Saving…' : 'Done ✓'}
                                </button>
                                <button className="btn btn--ghost btn--sm" onClick={() => onNavigateToDemo?.(demo.demo_id)}>View →</button>
                                <button className="btn btn--ghost btn--sm" onClick={() => openRemindPicker(pickerKey)}>⏰ Remind Later</button>
                                <button className="btn btn--ghost btn--sm" style={{ color: 'var(--text-3)' }} onClick={() => dismissNextStepFn(demo.demo_id)}>Dismiss</button>
                              </div>
                              {remindPickerKey === pickerKey && (
                                <RemindLaterPicker
                                  todayStr={todayStr}
                                  onConfirm={(date) => confirmRemindLater(pickerKey, date)}
                                  onCancel={() => setRemindPickerKey(null)}
                                />
                              )}
                            </div>
                          }
                        />
                      );
                    })}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Cold Outreach ── */}
        <div className="lane">
          <div className="lane-header">
            <div className="lane-header__title">Cold Outreach</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <LaneChip tone="gray">{outreachBatches.length} batch{outreachBatches.length !== 1 ? 'es' : ''}</LaneChip>
            </div>
          </div>
          <div className="lane-body">
            {cadenceActionList.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div className="sub-label">Cadence Follow-ups Due</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {cadenceActionList.slice(0, 5).map((a, i) => {
                    const tone = a.status === 'overdue' ? 'red' : a.status === 'due_soon' ? 'amber' : 'gray';
                    const dueStr = a.dueDate ? new Date(a.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                    return (
                      <div key={`${a.listName}-${a.stage}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                        <span className="chip chip-purple" style={{ fontSize: 10 }}>{a.listName}</span>
                        <span style={{ color: 'var(--text-2)', flex: 1 }}>{a.label}</span>
                        <span className={`chip chip-${tone}`} style={{ fontSize: 10 }}>{tone === 'red' ? 'Overdue' : tone === 'amber' ? 'Due soon' : 'Upcoming'} · {dueStr}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {specRotationNext && (
              <div style={{ marginBottom: 12 }}>
                <div className="sub-label">Spec Rotation</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <span className="chip chip-purple" style={{ fontSize: 10 }}>Next up</span>
                  <span style={{ color: 'var(--text-2)', flex: 1 }}>{specRotationNext.industry}</span>
                  <span className="chip chip-gray" style={{ fontSize: 10 }}>
                    due {specRotationNext.dueDate ? new Date(specRotationNext.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                </div>
              </div>
            )}
            <div className="sub-label">Outreach Batches</div>
            {bdStatsError && (
              <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 8 }}>
                ⚠ Couldn't load BigDough engagement stats — followup lists will export without open/click data.
              </div>
            )}
            {outreachBatches.length === 0 ? (
              <div className="empty-lane"><div className="empty-lane__icon">📬</div><div>No outreach batches yet</div></div>
            ) : (
              outreachBatches.map((batch) => {
                const isExpanded = !!expandedBatches[batch.key];

                const batchThreadIds = new Set(batch.rows.map((r) => r.gmail_thread_id).filter(Boolean));
                const batchContactIds = new Set(batch.rows.map((r) => r.contact_id).filter(Boolean));
                const batchDay = batch.day;
                const repliedInbound = effectiveOutreachLog.filter((r) => {
                  if (String(r.direction ?? '').toLowerCase() !== 'inbound') return false;
                  if (batchThreadIds.size > 0 && r.gmail_thread_id && batchThreadIds.has(r.gmail_thread_id)) return true;
                  if (batchContactIds.has(r.contact_id)) {
                    return (r.date ?? '').slice(0, 10) >= batchDay;
                  }
                  return false;
                });
                const seenReplyContacts = new Set();
                const repliedContacts = repliedInbound.reduce((acc, r) => {
                  const cid = r.contact_id;
                  if (!cid || seenReplyContacts.has(cid)) return acc;
                  seenReplyContacts.add(cid);
                  const c = contactById[cid] ?? null;
                  const firm = firmMap[c?.firm_id ?? r.firm_id] ?? {};
                  acc.push({ contact: c, firm, log: r });
                  return acc;
                }, []);

                const totalReplied = repliedContacts.length;
                const needsActionCount = Math.max(0, batch.rows.length - totalReplied);
                const hasOnlyReplied = totalReplied > 0 && needsActionCount === 0;
                const batchStatus = hasOnlyReplied ? 'All replied' : 'Follow up needed';
                const batchStatusTone = hasOnlyReplied ? 'green' : 'amber';
                // Cadence list tag — deterministic (from the BD upload's list_name), not
                // the fuzzy subject/date match used for the delivered-count stat above.
                const batchListName = batch.rows.find((r) => r.list_name)?.list_name || '';

                return (
                  <div key={batch.key} className={`batch-card${hasOnlyReplied ? ' batch-card--replied' : ''}`}>
                    <div
                      className={`batch-header${isExpanded ? ' open' : ''}`}
                      onClick={() => setExpandedBatches((p) => ({ ...p, [batch.key]: !p[batch.key] }))}
                    >
                      <div className="batch-header__subject-row">
                        <span className="batch-chevron">{isExpanded ? '▾' : '▸'}</span>
                        <span className="batch-subject" title={batch.subject}>{batch.subject}</span>
                      </div>
                      <div className="batch-meta">
                        {batchListName && <span className="chip chip-purple" style={{ fontSize: 10 }} title="Cadence list">{batchListName}</span>}
                        <span className="chip chip-gray" style={{ fontSize: 10 }}>{batchDeliveredCount.get(batch.key) ?? batch.rows.length} delivered</span>
                        <span className={`chip chip-${batchStatusTone}`} style={{ fontSize: 10 }}>{totalReplied} replied</span>
                        <span className={`chip chip-${batchStatusTone}`} style={{ fontSize: 10 }}>{batchStatus}</span>
                        <span className="batch-date">{batch.day}</span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="batch-body">
                        {repliedContacts.length === 0 ? (
                          <div style={{ fontSize: 11, color: 'var(--text-3)', padding: '4px 2px' }}>
                            {hasOnlyReplied ? 'Replies matched, but no contact names were resolved.' : 'No matched replies yet'}
                          </div>
                        ) : (
                          <>
                            {repliedContacts.slice(0, 10).map(({ contact: c, firm, log: r }, i) => {
                              const rawName = String(c?.name ?? '').trim();
                              const looksLikeId = /^c_\d+$/i.test(rawName) || rawName.length < 2;
                              const displayName = (!looksLikeId && rawName) || c?.email || 'Unknown lead';
                              const clickTarget = c ?? { contact_id: r.contact_id };
                              return (
                                <div
                                  key={i}
                                  className="batch-contact-row"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => onOpenTimeline?.(clickTarget)}
                                  title="Open contact details"
                                >
                                  <span className="batch-contact-name">{displayName}</span>
                                  {firm.firm_name && <span className="batch-contact-firm">{firm.firm_name}</span>}
                                </div>
                              );
                            })}
                            {repliedContacts.length > 10 && (
                              <div className="batch-contact-row" style={{ justifyContent: 'center', color: 'var(--text-3)', fontSize: 11, cursor: 'default', borderStyle: 'dashed' }}>
                                +{repliedContacts.length - 10} more
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    <div className="batch-actions">
                      <div className="batch-actions__row">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <input
                            className="batch-remind-input"
                            type="date"
                            min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
                            value={reminderInput[batch.key] ?? ''}
                            onChange={(e) => setReminderInput((p) => ({ ...p, [batch.key]: e.target.value }))}
                          />
                          {reminderInput[batch.key] && (() => {
                            const days = Math.round((new Date(reminderInput[batch.key]) - new Date(todayStr)) / 86_400_000);
                            return days > 0 ? <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>in {days} day{days !== 1 ? 's' : ''}</span> : null;
                          })()}
                        </div>
                        <button className="btn btn--ghost btn--sm" style={{ fontSize: 11, whiteSpace: 'nowrap' }} onClick={() => saveBatchReminder(batch.key)}>
                          ⏰ Set
                        </button>
                        <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={() => dismissBatch(batch.key)}>
                          Dismiss
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                          title={batchUploadMap.has(batch.key)
                            ? 'Export followup list with open/click engagement from BigDough'
                            : 'No matching BigDough upload — exports a basic list from the outreach log'}
                          onClick={() => {
                            setFollowupCategories({ high_intent: true, opened: true, no_engagement: true, replied: false });
                            setFollowupModal({ batch, uploadId: batchUploadMap.get(batch.key) });
                          }}
                        >
                          📋 Followup List
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {urgentContacts.length === 0 && trialMissions.length === 0 && todayDemoRows.length === 0 && (
        <div className="empty-state" style={{ padding: '40px 24px' }}>
          <div className="empty-state__icon">✅</div>
          <div className="empty-state__title">All clear — nothing urgent today</div>
          <div className="empty-state__sub">Check back after the nightly sync or this morning's BigDough upload</div>
        </div>
      )}

      {/* XP toast */}
      {xpBump && (
        <div style={{ position: 'fixed', bottom: 28, right: 28, zIndex: 1100, background: 'var(--orange)', color: '#fff', borderRadius: 999, padding: '10px 20px', fontSize: 14, fontWeight: 700, boxShadow: '0 4px 16px rgba(0,0,0,.18)', display: 'flex', alignItems: 'center', gap: 10, animation: 'fadeInUp .25s ease' }}
          onClick={() => setXpBump(null)}>
          <span>✦ +{xpBump.xp_earned} XP</span>
          <span style={{ opacity: .7, fontWeight: 400 }}>·</span>
          <span>{xpBump.streak_days}d streak 🔥</span>
          <span style={{ marginLeft: 6, opacity: .6, fontSize: 11, fontWeight: 400 }}>tap to dismiss</span>
        </div>
      )}

      {/* Demo Outcome Modal */}
      {followupModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '24px 28px', minWidth: 320, maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Print Followup List</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {followupModal.batch.subject}
            </div>
            {!followupModal.uploadId && (
              <div style={{ fontSize: 11, color: 'var(--text-3)', background: 'var(--surface-2, rgba(0,0,0,0.04))', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', marginBottom: 12, lineHeight: 1.4 }}>
                No matching BigDough upload found for this batch, so open/click engagement isn't available. The list will only split contacts into <b>Replied</b> vs <b>No engagement</b>.
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>Include:</div>
            {[
              { key: 'high_intent',   label: 'High intent',     desc: 'Opened 2+ times or clicked links, no reply' },
              { key: 'opened',        label: 'Opened',          desc: 'Opened once, no reply' },
              { key: 'no_engagement', label: 'No engagement',   desc: 'Never opened, no reply' },
              { key: 'replied',       label: 'Replied',         desc: 'Already replied' },
            ].map(({ key, label, desc }) => {
              const unavailable = !followupModal.uploadId && (key === 'high_intent' || key === 'opened');
              return (
              <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, cursor: unavailable ? 'not-allowed' : 'pointer', opacity: unavailable ? 0.4 : 1 }}>
                <input
                  type="checkbox"
                  disabled={unavailable}
                  checked={!unavailable && !!followupCategories[key]}
                  onChange={(e) => setFollowupCategories((p) => ({ ...p, [key]: e.target.checked }))}
                  style={{ marginTop: 2 }}
                />
                <span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'block' }}>{desc}</span>
                </span>
              </label>
            );})}
            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              <button className="btn btn--ghost btn--sm" onClick={() => setFollowupModal(null)} disabled={followupLoading}>Cancel</button>
              <button
                className="btn btn--primary btn--sm"
                disabled={followupLoading || !Object.values(followupCategories).some(Boolean)}
                onClick={() => handleExportFollowup(followupModal.batch, followupModal.uploadId, followupCategories)}
              >
                {followupLoading ? 'Exporting…' : 'Export Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {demoOutcomeTarget && getToken && (
        <DemoOutcomeModal
          demo={demoOutcomeTarget.demo}
          contact={demoOutcomeTarget.contact}
          firm={demoOutcomeTarget.firm}
          getToken={getToken}
          onClose={() => setDemoOutcomeTarget(null)}
          onSaved={handleDemoOutcomeSaved}
          onStartTrial={(ctx) => {
            const { demo, contact, firm } = demoOutcomeTarget;
            setDemoOutcomeTarget(null);
            setCreateTrialTarget({ demo, contact, firm, outcomeText: ctx.outcome });
          }}
        />
      )}

      {/* Create Trial Modal */}
      {createTrialTarget && getToken && (
        <CreateTrialModal
          demo={createTrialTarget.demo}
          contact={createTrialTarget.contact}
          firm={createTrialTarget.firm}
          outcomeText={createTrialTarget.outcomeText}
          getToken={getToken}
          onClose={() => setCreateTrialTarget(null)}
          onSaved={handleDemoOutcomeSaved}
          onRefreshAll={onRefreshAll}
          onTabChange={onTabChange}
          trialDefaultDays={Number(settings?.trialDefaultDays ?? 30)}
        />
      )}

      {/* Create Trial from Next Step */}
      {nextStepTrialTarget && getToken && (
        <CreateTrialModal
          demo={nextStepTrialTarget.demo}
          contact={nextStepTrialTarget.contact}
          firm={nextStepTrialTarget.firm}
          outcomeText={nextStepTrialTarget.demo.next_step || ''}
          getToken={getToken}
          onClose={() => setNextStepTrialTarget(null)}
          onSaved={() => { setNextStepTrialTarget(null); onRefreshOutreach?.(); onRefreshAll?.(); }}
          onTabChange={onTabChange}
          trialDefaultDays={Number(settings?.trialDefaultDays ?? 30)}
        />
      )}

      {/* Quick Log Modal */}
      {quickLogTarget && getToken && (
        <QuickLogModal
          contact={quickLogTarget.contact}
          firmName={quickLogTarget.firmName}
          getToken={getToken}
          onClose={() => setQuickLogTarget(null)}
          onSaved={handleLogSaved}
        />
      )}
    </div>
  );
}
